import { useCallback, useEffect, useMemo, useState } from "react";

import {
  useEditorFocus,
  useEmoji,
  useMentionAtom,
  useRemirrorContext,
} from "@remirror/react";

import type { Preset } from "simplydo/react/richtext/Remirror/types";

import type { RemirrorMention } from ".";
import { useFocusState } from "../useFocusState";

type MentionConfigEntry = Preset[number]["config"] & {
  name?: string;
  char?: string;
  filter?: (mention: RemirrorMention, query: string) => boolean;
  formatMention?: (item: Record<string, unknown>) => RemirrorMention;
};

type FilterParameter = {
  name: string;
  query: string;
  addMention: (mention: RemirrorMention) => void;
  char: string;
};

type EmojiSuggestion = {
  id: string;
  emoji: string;
  label: string;
  description?: string;
};

const defaultMentionFormat = (item: Record<string, unknown>) => ({
  ...item,
  appendText: " ",
});

const emojiMentionFormat = (emoji: {
  hexcode: string;
  emoji: string;
  shortcodes: string[];
  annotation?: string;
}): EmojiSuggestion => ({
  id: emoji.hexcode,
  emoji: emoji.emoji,
  label: `:${emoji.shortcodes[0]}:`,
  description: emoji.annotation,
});

const defaultFilter = () => true;

const filterMentions = (
  parameter: FilterParameter | null,
  allMentions: Record<string, Array<RemirrorMention>>,
  config: Record<string, MentionConfigEntry>,
) => {
  if (!parameter || !allMentions) {
    return [];
  }

  const { name, query } = parameter;
  const theConfig = config[name];
  if (!theConfig) {
    return [];
  }

  let result: Array<RemirrorMention> = [];
  const { char } = theConfig;
  Object.values(config).forEach((c) => {
    if (c.char === char) {
      const { filter = defaultFilter, formatMention = defaultMentionFormat } =
        c;
      const configName = c.name ?? name;
      result = result.concat(
        (allMentions[configName] ?? [])
          .map((mention) => ({
            ...formatMention(mention),
            name: configName,
            priority: mention.priority,
            appendText: " ",
          }))
          .filter((t) => filter(t, query)),
      );
    }
  });
  return result.sort((a, b) => b.priority - a.priority);
};

const createFilterParameter = (
  state: {
    name: string;
    query?: { full?: string };
    command: (mention: RemirrorMention) => void;
    text?: { full?: string };
  } | null,
): FilterParameter | null =>
  state ?
    {
      name: state.name,
      query: state?.query?.full ?? "",
      addMention: state.command,
      char: state?.text?.full?.charAt(0) ?? "",
    }
  : null;

type UseCustomMentionProps = {
  config: Record<string, MentionConfigEntry>;
  allMentions: Record<string, Array<RemirrorMention>>;
  onSuggest: (names: string[], query: string) => void;
  onMentionSelect: (mention: RemirrorMention) => void;
};

const useCustomMention = ({
  config,
  allMentions,
  onSuggest,
  onMentionSelect,
}: UseCustomMentionProps) => {
  const [_, focus] = useEditorFocus();
  const isFocused = useFocusState();

  const rem = useRemirrorContext();
  const emojisEnabled = useMemo(() => "emoji" in config, [config]);
  const getFilteredMentions = useCallback(
    (param: FilterParameter | null) =>
      filterMentions(param, allMentions, config),
    [config, allMentions],
  );
  const [filterParameter, setFilterParameter] =
    useState<FilterParameter | null>(null);
  const [loading, setLoading] = useState(false);
  const [isNewLoad, setIsNewLoad] = useState(false);
  const [mentions, setMentions] = useState<Array<RemirrorMention>>([]);
  const mentionState = useMentionAtom({ items: mentions });
  const emojiState = useEmoji();

  const configNamesForChar: Record<string, Array<string>> = useMemo(() => {
    const names: Record<string, Array<string>> = {};
    Object.values(config).forEach((c) => {
      if (!c.char || !c.name) {
        return;
      }

      if (!names[c.char]) {
        names[c.char] = [];
      }
      names[c.char]?.push(c.name);
    });
    return names;
  }, [config]);

  useEffect(() => {
    const newParameter = createFilterParameter(
      mentionState?.state as Parameters<typeof createFilterParameter>[0],
    );
    const didChange =
      filterParameter?.name !== newParameter?.name ||
      filterParameter?.query !== newParameter?.query;

    if (didChange) {
      if (newParameter && onSuggest) {
        setLoading(true);
        if (!filterParameter) {
          setIsNewLoad(true);
        }

        const paramNames = configNamesForChar[newParameter.char] ?? [];
        onSuggest(paramNames, newParameter.query);
      }
      setFilterParameter(newParameter);
    }
  }, [mentionState?.state, filterParameter, onSuggest, configNamesForChar]);

  useEffect(() => {
    setMentions(getFilteredMentions(filterParameter));
  }, [filterParameter, getFilteredMentions]);

  useEffect(() => {
    setLoading(false);
    setIsNewLoad(false);
  }, [allMentions]);

  const createMention = useCallback(
    (index: number) => {
      if (mentions.length === 0 || index >= mentions.length) {
        return;
      }
      if (!filterParameter) {
        return;
      }

      const mention = mentions[index];
      if (!mention) {
        return;
      }
      if (onMentionSelect) {
        onMentionSelect(mention);
      }
      filterParameter.addMention(mention);
      setTimeout(() => {
        focus();
        rem.commands.emptyUpdate();
      }, 0);
    },
    [rem, filterParameter, mentions, focus, onMentionSelect],
  );

  if (emojisEnabled && emojiState?.state?.list?.length) {
    return {
      suggestions: emojiState.state.list.map(emojiMentionFormat),
      activeIndex: emojiState.index || 0,
      createMention: (index: number) => {
        const emoji = emojiState.state?.list?.[index]?.emoji;
        if (emoji) {
          emojiState.state.apply(emoji);
        }
        setTimeout(() => !isFocused && focus(), 0);
      },
      isActive: isFocused,
      loading: false,
    };
  }

  return {
    suggestions: isNewLoad ? [] : mentions,
    activeIndex: (mentionState?.index as number) || 0,
    createMention,
    isActive: !!mentionState?.state && isFocused,
    loading,
  };
};

export default useCustomMention;
